/**
 * Shared batch packaging calls used by Batch Details and the Facility View
 * drag-to-package modal. Same API and rules as the original Batch Details flow:
 * keg package types need exactly `quantity` scanned empty kegs; others are qty only.
 */
import { apiFetch } from './api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from './kegCode';

export const isKegPackageType = (type: string) => /keg/i.test(type) && !/shell/i.test(type);

/** Validate a scanned keg: real tag, exists, Empty, not already added. Returns the normalized code. */
export async function checkKegForPackaging(raw: string, existing: string[]): Promise<{ code?: string; error?: string }> {
  const code = normalizeKegCode(raw);
  if (!isValidKegCode(code)) return { error: 'Valid keg tag needed (e.g., EK09503)' };
  try {
    const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Keg not found: HTTP ${res.status}, ${text}`);
    }
    const keg = await res.json();
    if (keg.status !== 'Empty') return { error: `Keg ${code} is not empty` };
    if (existing.map((c) => normalizeKegCode(c)).includes(code)) return { error: `Keg ${code} already added` };
    return { code };
  } catch (err: any) {
    return { error: 'Failed to add keg: ' + err.message };
  }
}

export interface PackagingRequest {
  packageType: string;
  quantity: number;
  locationId: number | string | null;
  kegCodes: string[];
}

/** POST /api/batches/:id/package. Returns the JSON (may be { prompt: 'volumeAdjustment', ... }). */
export async function postBatchPackaging(batchId: string, req: PackagingRequest, failPrefix = 'Failed to package') {
  const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/package`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      packageType: req.packageType,
      quantity: req.quantity,
      locationId: req.locationId ? parseInt(String(req.locationId), 10) : null,
      kegCodes: req.kegCodes,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    // Show the server's reason in full (e.g. over the actual volume left in the tank).
    let reason = '';
    try {
      reason = String(JSON.parse(text)?.error || '');
    } catch {
      reason = '';
    }
    throw new Error(reason ? `${failPrefix}: ${reason}` : `${failPrefix}: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
  }
  return res.json();
}

/**
 * Packaging shortfall: record an upward Brite WIP production adjustment of
 * exactly `shortfall` bbl (auditable cellar event + batch action), then package.
 */
export async function adjustVolumeAndPackage(
  batchId: string,
  _currentVolume: number,
  shortfall: number,
  req: PackagingRequest
) {
  const z = Number(shortfall);
  if (!Number.isFinite(z) || z <= 0) {
    throw new Error('Shortfall must be a positive number of barrels');
  }
  const res = await apiFetch(`${API_BASE_URL}/api/batches/${batchId}/adjust-volume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      adjustmentBbl: z,
      reason: 'Brite WIP adjustment for packaging shortfall',
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    let reason = '';
    try {
      reason = String(JSON.parse(text)?.error || '');
    } catch {
      reason = '';
    }
    throw new Error(
      reason
        ? `Failed to adjust volume: ${reason}`
        : `Failed to adjust volume: HTTP ${res.status}, Response: ${text.slice(0, 50)}`
    );
  }
  return postBatchPackaging(batchId, req, 'Failed to package after adjustment');
}

export interface ProductPackageChoice {
  /** Packaging type posted to /api/batches/:id/package (server maps it to the product's FG item). */
  type: string;
  /** Finished-goods item name shown to the user, e.g. "Hazy Train IPA (Keg - 1/2 bbl)". */
  label: string;
  /** Package volume in bbl when known from /api/package-types. */
  volume: number | null;
  keg: boolean;
}

/**
 * Only the product's own configured package types, labeled with its finished-goods
 * item name. Generic /api/package-types is used only for the volume hint.
 */
export function productPackageChoices(
  productPackageTypes: { type: string; itemName?: string | null; enabled?: number | boolean | null }[] | null | undefined,
  packageTypes: { name: string; volume: number; enabled?: number | boolean }[]
): ProductPackageChoice[] {
  const seen = new Set<string>();
  const out: ProductPackageChoice[] = [];
  for (const p of productPackageTypes || []) {
    const type = String(p?.type || '').trim();
    if (!type || p.enabled === 0 || p.enabled === false || seen.has(type.toLowerCase())) continue;
    seen.add(type.toLowerCase());
    const pt = packageTypes.find((g) => g.name.toLowerCase() === type.toLowerCase());
    out.push({
      type,
      label: String(p.itemName || '').trim() || type,
      volume: pt && Number.isFinite(Number(pt.volume)) ? Number(pt.volume) : null,
      keg: isKegPackageType(type),
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export const choiceLabel = (c: ProductPackageChoice) =>
  c.volume != null ? `${c.label} (${(c.volume * 31).toFixed(2)} gal)` : c.label;

/** Resolve the batch's product: by id first (ids are large numbers, compare numerically), else by name. */
export function findBatchProduct<T extends { id?: number | string; name?: string }>(
  products: T[],
  batch: { productId?: number | string | null; productName?: string | null }
): T | undefined {
  const byId = batch.productId != null ? products.find((p) => p.id != null && String(p.id) === String(batch.productId)) : undefined;
  if (byId) return byId;
  const name = (batch.productName || '').trim().toLowerCase();
  return name ? products.find((p) => (p.name || '').trim().toLowerCase() === name) : undefined;
}
